import { formatGr, normKey } from '@odhaczka/core';
import { useState } from 'react';
import { Link } from 'react-router';
import { articleOptions, setItemArticle, useArticleIndex } from '../../data/articles';
import { agoLabel, articleFor, articleName, type PriceIndex } from '../../data/prices';
import { PROMO_SOURCE, promoShort } from '../../data/promotions';
import { linePaidGr } from '../../data/receipts';
import type { ListItemRow, PromotionRow, ReceiptLineRow } from '../../db/types';

/** Krótka cena przy wierszu: kwota z paragonu (kupione) albo ostatnia cena półkowa. */
export function rowPriceLabel(item: ListItemRow, index: PriceIndex | undefined, lines: readonly ReceiptLineRow[] | undefined): string | null {
  if (lines && lines.length > 0) return formatGr(lines.reduce((s, l) => s + linePaidGr(l), 0));
  if (!index) return null;
  const a = articleFor(item, index);
  if (!a || a.lastShelfGr === null) return null;
  return `${formatGr(a.lastShelfGr)}${a.weight ? '/kg' : ''}`;
}

/** Ceny w arkuszu pozycji: co zapłacono (paragon tej listy) i ostatnia cena z paragonów. */
export function ItemPriceInfo({
  item,
  index,
  lines,
  promos = [],
  allowPin = false,
}: {
  item: ListItemRow;
  index: PriceIndex | undefined;
  lines: readonly ReceiptLineRow[] | undefined;
  /** Promocje trwające teraz na artykuły pozycji. */
  promos?: readonly PromotionRow[];
  /** Można przypiąć konkretny towar z paragonów (lista do Lidla albo bez sklepu). */
  allowPin?: boolean;
}) {
  const a = index ? articleFor(item, index) : null;
  const paid = lines && lines.length > 0 ? lines.reduce((s, l) => s + linePaidGr(l), 0) : null;
  if (!a && paid === null) return allowPin ? <PinArticle item={item} /> : null;
  const promoLines = promos.slice(0, 3).map((p) => (
    <p key={p.id} className="text-amber-800 dark:text-amber-200">
      <b>%</b> {p.title ? `${p.title}: ` : ''}
      {promoShort(p)}
      {p.priceGr ? ` · ${formatGr(p.priceGr)}` : ''}
      <span className="text-stone-500"> ({PROMO_SOURCE[p.source ?? ''] ?? 'promocja'})</span>
    </p>
  ));
  const per = a?.weight ? '/kg' : '';
  const href = a ? (a.productId && !item.articleId ? `/products/${encodeURIComponent(index!.resolveProduct(a.productId))}` : `/articles/${encodeURIComponent(a.id)}`) : null;
  return (
    <div className="mb-1 rounded-xl bg-stone-50 px-3 py-2 text-sm dark:bg-stone-800/60">
      {promoLines}
      {paid !== null && (
        <p>
          Zapłacone <b className="tabular-nums">{formatGr(paid)}</b>
          {lines!.length === 1 && lines![0]!.discountGr ? <span className="text-stone-500"> (rabat {formatGr(lines![0]!.discountGr)})</span> : null}
        </p>
      )}
      {a && a.lastShelfGr !== null && (
        <p className="text-stone-600 dark:text-stone-400">
          {paid !== null ? 'Ostatnia cena' : 'Cena'}{' '}
          <b className="text-stone-900 tabular-nums dark:text-stone-100">
            {formatGr(a.lastShelfGr)}
            {per}
          </b>
          {a.lastPaidGr !== null && a.lastPaidGr < a.lastShelfGr && (
            <span className="tabular-nums"> · zapłacone {formatGr(a.lastPaidGr)}{per}</span>
          )}
          {a.lastBoughtMs ? <span> · w Lidlu {agoLabel(a.lastBoughtMs)}</span> : null}
          <span className="block truncate text-xs text-stone-500">{articleName(a)}</span>
        </p>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-x-4">
        {href && (
          <Link to={href} className="inline-block font-medium text-brand-700 underline underline-offset-2 dark:text-brand-500">
            Historia ceny
          </Link>
        )}
        {allowPin && <PinArticle item={item} inline />}
      </div>
    </div>
  );
}

/** Konkretny towar z paragonów dla pozycji: przypięcie zmienia cenę pozycji i pomaga dopasować paragon. */
function PinArticle({ item, inline = false }: { item: ListItemRow; inline?: boolean }) {
  const [open, setOpen] = useState(false);
  const index = useArticleIndex(open || !!item.articleId);
  const options = index ? articleOptions(index, normKey(item.name), item.productId, 6) : [];
  const pinned = item.articleId ? index?.byId.get(item.articleId) : undefined;
  const toggle = (
    <button type="button" onClick={() => setOpen((o) => !o)} className="min-h-9 font-medium text-brand-700 dark:text-brand-500">
      {pinned ? `Towar: ${articleName(pinned)} · zmień` : 'Wybierz konkretny towar'}
    </button>
  );
  if (!open) return inline ? toggle : <div className="mb-1 text-sm">{toggle}</div>;
  return (
    <div className={`${inline ? 'mt-1 w-full' : 'mb-1'} flex flex-col gap-1 text-sm`}>
      {toggle}
      {options.length === 0 && <p className="text-stone-500">Brak podobnych towarów w paragonach.</p>}
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const on = o.id === item.articleId;
          return (
            <button
              key={o.id}
              type="button"
              aria-pressed={on}
              onClick={() => void setItemArticle(item.id, on ? null : o.id).then(() => setOpen(false))}
              className={`min-h-9 rounded-full px-3 ${on ? 'bg-brand-700 font-medium text-white' : 'border border-stone-300 dark:border-stone-700'}`}
            >
              {on && '✓ '}
              {articleName(o)}
              {o.lastShelfGr !== null && <span className={on ? '' : 'text-stone-500'}> · {formatGr(o.lastShelfGr)}{o.weight ? '/kg' : ''}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
